/**
 * Payment Terms Service - Frontend API calls for payment terms operations
 * Uses shared API client from @/shared/api
 */

import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Get payment terms for a provider (with inheritance)
 * @param {string} providerId - Provider ID
 * @returns {Promise<Object>} Payment terms data with source information
 */
export const getPaymentTerms = async (providerId) => {
  try {
    const response = await api.get(`/providers/payment-terms/${providerId}`);
    logInfo("Payment terms fetched successfully", { providerId });
    return {
      success: true,
      data: response.data || response,
      source: response.source,
      inheritedFrom: response.inheritedFrom,
      isDefault: response.isDefault,
    };
  } catch (error) {
    logError("Failed to fetch payment terms", {
      providerId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Create payment terms for a provider
 * @param {Object} termsData - Payment terms data
 * @param {string} termsData.providerId - Provider ID
 * @param {string} termsData.payoutTrigger - Payout trigger type
 * @param {number} [termsData.payoutDelayDays] - Payout delay days
 * @param {number} termsData.retentionPercent - Retention percentage
 * @param {number} termsData.disputeWindowDays - Dispute window days
 * @returns {Promise<Object>} Creation result
 */
export const createPaymentTerms = async (termsData) => {
  try {
    const response = await api.post("/providers/payment-terms", termsData);
    logInfo("Payment terms created successfully", {
      providerId: termsData.providerId,
    });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to create payment terms", {
      termsData,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Update payment terms for a provider
 * @param {string} providerId - Provider ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise<Object>} Update result
 */
export const updatePaymentTerms = async (providerId, updates) => {
  try {
    const response = await api.put(`/providers/payment-terms/${providerId}`, updates);
    logInfo("Payment terms updated successfully", { providerId });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to update payment terms", {
      providerId,
      updates,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Delete payment terms for a provider
 * @param {string} providerId - Provider ID
 * @returns {Promise<Object>} Delete result
 */
export const deletePaymentTerms = async (providerId) => {
  try {
    const response = await api.delete(`/providers/payment-terms/${providerId}`);
    logInfo("Payment terms deleted successfully", { providerId });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to delete payment terms", {
      providerId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get payment terms templates
 * @returns {Promise<Object>} Templates list
 */
export const getPaymentTermsTemplates = async () => {
  try {
    const response = await api.get("/providers/payment-terms/templates");
    logInfo("Payment terms templates fetched successfully");
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to fetch payment terms templates", {
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Create payment terms from template
 * @param {string} providerId - Provider ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Creation result
 */
export const createPaymentTermsFromTemplate = async (providerId, templateId) => {
  try {
    const response = await api.post("/providers/payment-terms/from-template", {
      providerId,
      templateId,
    });
    logInfo("Payment terms created from template successfully", {
      providerId,
      templateId,
    });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to create payment terms from template", {
      providerId,
      templateId,
      error: error.message,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};
